/**
 * Toolbox Talk Drawer
 * Create / Edit toolbox talks with attendee tracking.
 */

import { Plus, Trash2, X } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { DatePicker, Input, NumberInput, SearchableDropdown, TextArea } from '../forms';
import { useToast } from '../../contexts/ToastContext';
import {
  useCreateToolboxTalkMutation,
  useUpdateToolboxTalkMutation,
} from '../../services/safetyApi';
import type {
  ToolboxTalk,
  ToolboxTalkCategory,
  TalkAttendee,
  CreateToolboxTalkRequest,
  UpdateToolboxTalkRequest,
} from '../../types/safety';

// ============================================================================
// Constants
// ============================================================================

const CATEGORY_OPTIONS = [
  { value: 'fall_protection', label: 'Fall Protection' },
  { value: 'ppe', label: 'PPE' },
  { value: 'electrical', label: 'Electrical Safety' },
  { value: 'excavation', label: 'Excavation & Trenching' },
  { value: 'scaffold', label: 'Scaffold Safety' },
  { value: 'fire', label: 'Fire Safety' },
  { value: 'chemical', label: 'Chemical / Hazmat' },
  { value: 'confined_space', label: 'Confined Space' },
  { value: 'heavy_equipment', label: 'Heavy Equipment' },
  { value: 'weather', label: 'Weather Awareness' },
  { value: 'housekeeping', label: 'Housekeeping' },
  { value: 'general', label: 'General Safety' },
];

// ============================================================================
// Props
// ============================================================================

interface ToolboxTalkDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  talk?: ToolboxTalk | null;
  mode: 'create' | 'edit';
}

// ============================================================================
// Component
// ============================================================================

const ToolboxTalkDrawer: React.FC<ToolboxTalkDrawerProps> = ({
  isOpen,
  onClose,
  projectId,
  talk,
  mode,
}) => {
  const toast = useToast();

  // Form state
  const [talkDate, setTalkDate] = useState('');
  const [topic, setTopic] = useState('');
  const [category, setCategory] = useState<ToolboxTalkCategory>('general');
  const [conductedByName, setConductedByName] = useState('');
  const [duration, setDuration] = useState<number | ''>('');
  const [location, setLocation] = useState('');
  const [materials, setMaterials] = useState('');
  const [notes, setNotes] = useState('');
  const [attendees, setAttendees] = useState<Omit<TalkAttendee, '_id'>[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [createTalk, { isLoading: isCreating }] = useCreateToolboxTalkMutation();
  const [updateTalk, { isLoading: isUpdating }] = useUpdateToolboxTalkMutation();

  const isSubmitting = isCreating || isUpdating;

  // Populate form when editing
  useEffect(() => {
    if (isOpen && talk && mode === 'edit') {
      setTalkDate(talk.talkDate?.split('T')[0] || '');
      setTopic(talk.topic || '');
      setCategory(talk.category);
      setConductedByName(talk.conductedByName || '');
      setDuration(talk.duration || '');
      setLocation(talk.location || '');
      setMaterials(talk.materials || '');
      setNotes(talk.notes || '');
      setAttendees(
        talk.attendees.map(a => ({
          userId: typeof a.userId === 'string' ? a.userId : undefined,
          name: a.name,
          company: a.company || '',
          trade: a.trade || '',
        }))
      );
    } else if (isOpen && mode === 'create') {
      setTalkDate(new Date().toISOString().split('T')[0]);
      setTopic('');
      setCategory('general');
      setConductedByName('');
      setDuration('');
      setLocation('');
      setMaterials('');
      setNotes('');
      setAttendees([]);
      setErrors({});
    }
  }, [isOpen, talk, mode]);

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!talkDate) errs.talkDate = 'Talk date is required';
    if (!topic.trim()) errs.topic = 'Topic is required';
    if (!category) errs.category = 'Category is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleAddAttendee = useCallback(() => {
    setAttendees(prev => [...prev, { name: '', company: '', trade: '' }]);
  }, []);

  const handleRemoveAttendee = useCallback((index: number) => {
    setAttendees(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleAttendeeChange = useCallback(
    (index: number, field: keyof Omit<TalkAttendee, '_id'>, value: string) => {
      setAttendees(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [field]: value };
        return updated;
      });
    },
    []
  );

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      const payload: CreateToolboxTalkRequest | UpdateToolboxTalkRequest = {
        talkDate,
        topic,
        category,
        conductedByName: conductedByName || undefined,
        duration: duration !== '' ? Number(duration) : undefined,
        location: location || undefined,
        materials: materials || undefined,
        notes: notes || undefined,
      };

      if (mode === 'create') {
        const createPayload: CreateToolboxTalkRequest = {
          ...(payload as CreateToolboxTalkRequest),
          attendees: attendees.filter(a => a.name.trim() !== ''),
        };
        await createTalk({ projectId, data: createPayload }).unwrap();
        toast.success('Toolbox talk created successfully');
      } else if (talk) {
        await updateTalk({
          projectId,
          talkId: talk._id,
          data: payload as UpdateToolboxTalkRequest,
        }).unwrap();
        toast.success('Toolbox talk updated successfully');
      }

      onClose();
    } catch (err: unknown) {
      const message =
        err &&
        typeof err === 'object' &&
        'data' in err &&
        typeof (err as { data?: { message?: string } }).data?.message === 'string'
          ? (err as { data: { message: string } }).data.message
          : 'Failed to save toolbox talk';
      toast.error(message);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]"
        onClick={onClose}
      />

      {/* Drawer - 60% width */}
      <div className="fixed top-6 right-6 bottom-6 z-[100] w-[60%] animate-in slide-in-from-right">
        <div className="bg-white rounded-2xl shadow-2xl h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {mode === 'create' ? 'New Toolbox Talk' : 'Edit Toolbox Talk'}
              </h2>
              {talk && (
                <p className="text-xs text-gray-500 mt-0.5">{talk.talkNumber}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">
            {/* Section: Talk Details */}
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">
                Talk Details
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Date <span className="text-red-500">*</span>
                    </label>
                    <DatePicker
                      value={talkDate}
                      onChange={setTalkDate}
                      placeholder="Select date"
                    />
                    {errors.talkDate && (
                      <p className="text-xs text-red-500 mt-1">{errors.talkDate}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Category <span className="text-red-500">*</span>
                    </label>
                    <SearchableDropdown
                      placeholder="Select category"
                      value={category}
                      onChange={val => setCategory(val as ToolboxTalkCategory)}
                      options={CATEGORY_OPTIONS}
                      searchable={false}
                    />
                    {errors.category && (
                      <p className="text-xs text-red-500 mt-1">{errors.category}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    Topic <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={topic}
                    onChange={e => setTopic(e.target.value)}
                    placeholder="e.g. Proper use of fall protection systems"
                  />
                  {errors.topic && (
                    <p className="text-xs text-red-500 mt-1">{errors.topic}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Conducted By</label>
                    <Input
                      value={conductedByName}
                      onChange={e => setConductedByName(e.target.value)}
                      placeholder="Presenter name"
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-gray-500 mb-1">
                      Duration (minutes)
                    </label>
                    <NumberInput
                      value={duration === '' ? undefined : duration}
                      onChange={val => setDuration(val ?? '')}
                      placeholder="e.g. 15"
                      min={1}
                      max={480}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-gray-500 mb-1">Location</label>
                  <Input
                    value={location}
                    onChange={e => setLocation(e.target.value)}
                    placeholder="e.g. Job site trailer, Break room"
                  />
                </div>
              </div>
            </div>

            {/* Section: Attendee List */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Attendees{' '}
                  <span className="font-normal text-gray-400">({attendees.length})</span>
                </h3>
                <button
                  onClick={handleAddAttendee}
                  className="inline-flex items-center text-xs font-medium text-brand-600 hover:text-brand-700"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Add Attendee
                </button>
              </div>

              {attendees.length === 0 ? (
                <div className="py-8 text-center border-2 border-dashed border-gray-200 rounded-lg">
                  <p className="text-sm text-gray-400">No attendees added</p>
                  <button
                    onClick={handleAddAttendee}
                    className="mt-2 text-xs text-brand-600 hover:underline"
                  >
                    Add first attendee
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Header row */}
                  <div className="grid grid-cols-10 gap-2 text-[10px] text-gray-400 uppercase tracking-wide px-1 pb-1 border-b border-gray-100">
                    <div className="col-span-4">Name</div>
                    <div className="col-span-3">Company</div>
                    <div className="col-span-2">Trade</div>
                    <div className="col-span-1"></div>
                  </div>
                  {attendees.map((attendee, index) => (
                    <div key={index} className="grid grid-cols-10 gap-2 items-center">
                      <div className="col-span-4">
                        <input
                          type="text"
                          value={attendee.name}
                          onChange={e => handleAttendeeChange(index, 'name', e.target.value)}
                          placeholder="Full name"
                          className="w-full text-xs border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-400"
                        />
                      </div>
                      <div className="col-span-3">
                        <input
                          type="text"
                          value={attendee.company || ''}
                          onChange={e => handleAttendeeChange(index, 'company', e.target.value)}
                          placeholder="Company"
                          className="w-full text-xs border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-400"
                        />
                      </div>
                      <div className="col-span-2">
                        <input
                          type="text"
                          value={attendee.trade || ''}
                          onChange={e => handleAttendeeChange(index, 'trade', e.target.value)}
                          placeholder="Trade"
                          className="w-full text-xs border border-gray-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-400"
                        />
                      </div>
                      <div className="col-span-1 flex justify-end">
                        <button
                          onClick={() => handleRemoveAttendee(index)}
                          className="p-1 text-gray-300 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={handleAddAttendee}
                    className="w-full py-2 text-xs text-gray-400 hover:text-brand-600 border-2 border-dashed border-gray-200 hover:border-brand-300 rounded-lg transition-colors"
                  >
                    <Plus className="h-3.5 w-3.5 inline mr-1" />
                    Add attendee
                  </button>
                </div>
              )}
            </div>

            {/* Section: Materials & Notes */}
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">
                Materials & Notes
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    Materials / Handouts
                  </label>
                  <TextArea
                    value={materials}
                    onChange={setMaterials}
                    placeholder="Describe materials, handouts, or slides used..."
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Notes</label>
                  <TextArea
                    value={notes}
                    onChange={setNotes}
                    placeholder="Additional notes about this toolbox talk..."
                    rows={3}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-8 py-5 bg-gray-50 flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting
                ? 'Saving...'
                : mode === 'create'
                ? 'Create Talk'
                : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default ToolboxTalkDrawer;
